import { computed, nextTick, ref, shallowRef, watch } from 'vue';
import type { Router } from 'vue-router';
import type { ObservationDraft, ServerObservationState, TableObservation } from '@yana/shared';
import { useRealtime } from './useRealtime';
import { useAuthStore } from '@/stores/auth';

export interface ObservationTableAdapter {
  getState: () => TableObservation | null;
  applyState: (state: TableObservation) => Promise<void> | void;
  captureLocal: () => unknown;
  restoreLocal: (saved: unknown) => Promise<void> | void;
  discardLocal?: () => void;
}

const target = ref<string | null>(null);
const targetTab = ref<string | null>(null);
const name = ref('');
const color = ref('#6366f1');
const paused = ref(false);
const restoring = ref(false);
const remote = shallowRef<ServerObservationState | null>(null);
const error = ref<string | null>(null);
const adapters = new Set<ObservationTableAdapter>();
const saved = new Map<string, unknown>();
const applied = new WeakMap<ObservationTableAdapter, string>();
let router: Router | null = null;
let originalPath: string | null = null;
let initialized = false;
let generation = 0;
let applying: Promise<void> | null = null;
let applyAgain = false;
let ownDraft: ObservationDraft | null = null;
let publishTimer: ReturnType<typeof setTimeout> | null = null;
let latestLocal: TableObservation | null = null;
let lastPayload = '';
let tabId: string | null = null;

function sessionTab(): string {
  if (tabId) return tabId;
  try {
    tabId = sessionStorage.getItem('yana.observation.tab');
    if (!tabId) {
      tabId = crypto.randomUUID();
      sessionStorage.setItem('yana.observation.tab', tabId);
    }
  } catch { tabId = crypto.randomUUID(); }
  return tabId!;
}

export function observationIdentity(state: TableObservation): string {
  return state.surface === 'sheet' ? `sheet:${state.sheetCode}` : 'candidates';
}
function pathFor(state: TableObservation): string {
  return state.surface === 'sheet' ? `/sheets/${encodeURIComponent(state.sheetCode!)}` : '/';
}

function publishNow(force = false): void {
  if (publishTimer) clearTimeout(publishTimer);
  publishTimer = null;
  const state = target.value || restoring.value ? null : latestLocal;
  const payload = JSON.stringify(state);
  if (!force && payload === lastPayload) return;
  lastPayload = payload;
  useRealtime().sendObservation({ type: 'observation.publish', tabId: sessionTab(), state });
}

function reportTable(state: TableObservation | null): void {
  latestLocal = state ? { ...state, draft: ownDraft } : null;
  if (publishTimer === null) publishTimer = setTimeout(() => publishNow(), 100);
}

function reportDraft(draft: ObservationDraft | null): void {
  ownDraft = draft ? { ...draft, text: draft.text.slice(0, 20_000) } : null;
  if (latestLocal) reportTable(latestLocal);
}

function capture(adapter: ObservationTableAdapter, state: TableObservation): void {
  const key = observationIdentity(state);
  if (!saved.has(key)) saved.set(key, adapter.captureLocal());
}

async function restoreMounted(consume = false): Promise<void> {
  for (const adapter of adapters) {
    const state = adapter.getState();
    if (!state) continue;
    const key = observationIdentity(state);
    if (saved.has(key)) {
      await adapter.restoreLocal(saved.get(key));
      if (consume) saved.delete(key);
    }
    applied.delete(adapter);
  }
}

async function applyLatest(): Promise<void> {
  const event = remote.value;
  if (!router || !target.value || paused.value || restoring.value || !useRealtime().isOnline.value || event?.status !== 'live' || !event.state) return;
  const epoch = generation;
  const path = pathFor(event.state);
  if (router.currentRoute.value.path !== path) {
    await restoreMounted();
    if (epoch !== generation || paused.value) return;
    await router.replace(path);
    await nextTick();
  }
  if (epoch !== generation || paused.value) return;
  for (const adapter of adapters) {
    const local = adapter.getState();
    if (!local || observationIdentity(local) !== observationIdentity(event.state)) continue;
    const key = `${epoch}:${event.tabId}:${event.revision}`;
    if (applied.get(adapter) === key) continue;
    capture(adapter, local);
    applied.set(adapter, key);
    await adapter.applyState(event.state);
  }
}

function requestApply(): void {
  if (applying) { applyAgain = true; return; }
  applying = (async () => {
    do {
      applyAgain = false;
      try { await applyLatest(); }
      catch {
        error.value = 'Не удалось открыть участок таблицы. Продолжите наблюдение, чтобы повторить.';
        paused.value = true;
      }
    } while (applyAgain && !restoring.value);
  })().finally(() => { applying = null; });
}

function receive(event: ServerObservationState): void {
  if (event.userId !== target.value) return;
  if (targetTab.value && event.tabId && targetTab.value !== event.tabId) return;
  if (event.tabId && event.tabId === remote.value?.tabId && event.revision < remote.value.revision) return;
  if (event.tabId) targetTab.value = event.tabId;
  remote.value = event;
  if (event.name) name.value = event.name;
  if (event.color) color.value = event.color;
  if (event.status === 'forbidden') {
    error.value = 'Нет доступа к наблюдению за этой таблицей.';
    paused.value = true;
  }
  requestApply();
}

/** The authenticated application owns the subscription across route changes. */
export function initializeObservation(navigation: Router): void {
  router = navigation;
  if (initialized) return;
  initialized = true;
  const realtime = useRealtime();
  realtime.onMessage((event) => {
    if (event.type === 'observation.state') receive(event);
    if (event.type === 'server.welcome') {
      lastPayload = '';
      publishNow(true);
      if (target.value) realtime.sendObservation({ type: 'observation.subscribe', userId: target.value, ...(targetTab.value ? { tabId: targetTab.value } : {}) });
    }
  });
  watch(() => useAuthStore().user?.id, (id, previous) => {
    if (id === previous) return;
    discardSession();
    ownDraft = null;
    latestLocal = null;
    lastPayload = '';
  });
  watch(() => useAuthStore().can('audit.view'), (allowed) => {
    if (!allowed && target.value) discardSession();
  });
  navigation.afterEach((to) => {
    if (target.value && !applying && !restoring.value && remote.value?.state && to.path !== pathFor(remote.value.state)) pause();
    if (to.name !== 'grid' && to.name !== 'sheet') reportTable(null);
  });
}

function discardSession(): void {
  const wasObserving = target.value !== null || saved.size > 0;
  generation++;
  saved.clear();
  originalPath = null;
  target.value = null;
  targetTab.value = null;
  remote.value = null;
  paused.value = false;
  ownDraft = null;
  latestLocal = null;
  error.value = null;
  if (publishTimer) clearTimeout(publishTimer);
  publishTimer = null;
  if (wasObserving) {
    for (const adapter of adapters) adapter.discardLocal?.();
    useRealtime().sendObservation({ type: 'observation.unsubscribe' });
  }
}

async function start(user: { userId: string; name: string; color?: string }): Promise<void> {
  if (!useAuthStore().can('audit.view') || user.userId === useAuthStore().user?.id) return;
  if (target.value) await stop();
  generation++;
  saved.clear();
  originalPath = router?.currentRoute.value.fullPath ?? '/';
  for (const adapter of adapters) {
    const state = adapter.getState();
    if (state) capture(adapter, state);
  }
  target.value = user.userId;
  targetTab.value = null;
  name.value = user.name;
  color.value = user.color ?? '#6366f1';
  paused.value = false;
  remote.value = null;
  error.value = null;
  ownDraft = null;
  publishNow(true);
  useRealtime().sendObservation({ type: 'observation.subscribe', userId: user.userId });
}

function pause(): void {
  if (!target.value || restoring.value) return;
  paused.value = true;
}

function resume(): void {
  if (!target.value) return;
  paused.value = false;
  error.value = null;
  generation++;
  useRealtime().sendObservation({ type: 'observation.subscribe', userId: target.value, ...(targetTab.value ? { tabId: targetTab.value } : {}) });
  requestApply();
}

async function stop(returnToOriginal = true): Promise<void> {
  if (restoring.value) return;
  generation++;
  restoring.value = true;
  useRealtime().sendObservation({ type: 'observation.unsubscribe' });
  try {
    await applying;
    await restoreMounted(true);
    if (returnToOriginal && router && originalPath && router.currentRoute.value.fullPath !== originalPath) {
      await router.replace(originalPath);
      await nextTick();
      // Tables that fetch on mount consume their saved position when ready.
      await restoreMounted(true);
    }
  } finally {
    target.value = null;
    targetTab.value = null;
    remote.value = null;
    paused.value = false;
    error.value = null;
    restoring.value = false;
    originalPath = null;
    ownDraft = null;
    const local = [...adapters].map((adapter) => adapter.getState()).find(Boolean) ?? null;
    reportTable(local);
    publishNow(true);
  }
}

export function registerObservationTable(adapter: ObservationTableAdapter): () => void {
  adapters.add(adapter);
  requestApply();
  return () => {
    adapters.delete(adapter);
    ownDraft = null;
    const other = [...adapters].map((entry) => entry.getState()).find(Boolean) ?? null;
    reportTable(other);
  };
}

export async function observationTableChanged(adapter: ObservationTableAdapter): Promise<void> {
  const epoch = generation;
  const state = adapter.getState();
  if (!target.value && !restoring.value && state) {
    const key = observationIdentity(state);
    if (saved.has(key)) {
      const snapshot = saved.get(key);
      saved.delete(key);
      await adapter.restoreLocal(snapshot);
    }
  }
  if (epoch !== generation || !adapters.has(adapter)) return;
  reportTable(adapter.getState());
  if (state) requestApply();
}

export function useObservation() {
  const active = computed(() => target.value !== null);
  const following = computed(() => active.value && !paused.value && !restoring.value && useRealtime().isOnline.value && remote.value?.status === 'live');
  return {
    active, following,
    paused: computed(() => paused.value),
    restoring: computed(() => restoring.value),
    name: computed(() => name.value), color: computed(() => color.value),
    state: computed(() => following.value ? remote.value?.state ?? null : null),
    status: computed(() => !useRealtime().isOnline.value ? 'disconnected' : remote.value?.status ?? 'connecting'),
    error: computed(() => error.value),
    start, pause, resume, stop, reportDraft,
  };
}
